/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 *
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.29.0
 */

import { IPageSubscriberIdTranslation } from '@/main/modules/internationalization/types/pages/subscriberId';
import { convertSecondsToMinutes } from '@/main/utils/convertSecondsToMinutes';
import { floor10 } from '@/main/utils/decimalsFormat';
import { getMultipleBytesWithUnits } from '@/main/utils/getMultipleBytesWithUnits';
import { convertDateFormatToOtherDateFormat, EDateFormat } from '@utils/convertDateFormatToOtherDateFormat';

import { EUnitOfMeasure, IInfoTariffDiscount, TInfoTariffRatePlanRemain } from '../../types';

const mapRemainToDiscount = (
    remain: TInfoTariffRatePlanRemain,
    translation: IPageSubscriberIdTranslation['tabs']['info']['units'],
): IInfoTariffDiscount => {
    const discount: IInfoTariffDiscount = {
        isUnlimitedVolume: false,
        label: remain.name,
        unit: '',
        unitTotal: '',
        value: 0,
        valueWithUnits: '',
        valuePercentage: 0,
        valueTotal: 0,
        dateTo:
            remain.dateTo && convertDateFormatToOtherDateFormat(remain.dateTo, EDateFormat.DATE_TIME, EDateFormat.DATE),
    };

    if (remain.isUnlimitedVolume) {
        discount.isUnlimitedVolume = remain.isUnlimitedVolume;
        discount.label = remain.name;
    } else {
        discount.isShared = remain.isShared;
        discount.isSpend = remain.isSpend;

        if (remain.unitOfMeasure === EUnitOfMeasure.BYTE) {
            const formattedAvailableVolume = getMultipleBytesWithUnits(remain.availableVolume);
            const formattedBaseVolume = getMultipleBytesWithUnits(remain.baseVolume);

            discount.unit = translation[formattedAvailableVolume.units];
            discount.unitTotal = translation[formattedBaseVolume.units];

            // нужен реформат кода этого компонента, чтобы уйти от использования этой переменной
            const availableVolumeWithUnits = `${formattedAvailableVolume.amount} ${translation[formattedAvailableVolume.units]}`

            discount.valueWithUnits = availableVolumeWithUnits;
            discount.value = formattedAvailableVolume.amount;
            discount.valueTotal = formattedBaseVolume.amount;

            const formattedAvailableVolumeLider = getMultipleBytesWithUnits(remain.availableVolumeLider ?? 0);
            const formattedBaseVolumeLider = getMultipleBytesWithUnits(remain.baseVolumeLider ?? 0);

            // нужен реформат кода этого компонента, чтобы уйти от использования этой переменной
            const availableVolumeWithUnitsLider = `${formattedAvailableVolumeLider.amount} ${translation[formattedAvailableVolumeLider.units]}`

            discount.availableVolumeLider = availableVolumeWithUnitsLider;
            discount.availableUnitsLider = translation[formattedAvailableVolumeLider.units];
            discount.baseVolumeLider = formattedBaseVolumeLider.amount;
            discount.baseUnitsLider = translation[formattedBaseVolumeLider.units];
            discount.valuePercentage = (remain.availableVolume / remain.baseVolume) * 100;
        } else if (remain.unitOfMeasure === EUnitOfMeasure.SECOND) {
            const formattedBaseVolume = convertSecondsToMinutes(remain.baseVolume);
            let formattedAvailableVolume = convertSecondsToMinutes(remain.availableVolume, {
                roundType: 'round',
                exp: -2,
            });

            if (formattedAvailableVolume > formattedBaseVolume) {
                formattedAvailableVolume = floor10(formattedAvailableVolume) as number;
            }

            const formattedBaseVolumeLider = convertSecondsToMinutes(remain.baseVolumeLider ?? 0);
            let formattedAvailableVolumeLider = convertSecondsToMinutes(remain.availableVolumeLider ?? 0, {
                roundType: 'round',
                exp: -2,
            });

            if (formattedAvailableVolumeLider > formattedBaseVolumeLider) {
                formattedAvailableVolumeLider = floor10(formattedAvailableVolume) as number;
            }

            discount.value = formattedAvailableVolume;
            discount.valueTotal = formattedBaseVolume;
            discount.availableVolumeLider = formattedAvailableVolumeLider;
            discount.baseVolumeLider = formattedBaseVolumeLider;
            discount.unit = translation['minute'];
            discount.unitTotal = translation['minute'];
            discount.valuePercentage = (remain.availableVolume / remain.baseVolume) * 100;
        } else if (remain.unitOfMeasure === EUnitOfMeasure.PIECE) {
            discount.label = remain.name;
            discount.unit = translation['piece'];
            discount.unitTotal = translation['piece'];
            discount.value = remain.availableVolume;
            discount.valueTotal = remain.baseVolume;
            discount.availableVolumeLider = remain.availableVolumeLider;
            discount.baseVolumeLider = remain.baseVolumeLider;
            discount.valuePercentage = (remain.availableVolume / remain.baseVolume) * 100;
        } else {
            discount.label = remain.name;
            discount.unit = remain.unitOfMeasure || '';
            discount.unitTotal = remain.unitOfMeasure || '';
            discount.value = remain.availableVolume;
            discount.valueTotal = remain.baseVolume;
            discount.availableVolumeLider = remain.availableVolumeLider;
            discount.baseVolumeLider = remain.baseVolumeLider;
            discount.valuePercentage = (remain.availableVolume / remain.baseVolume) * 100;
        }
    }

    return discount;
};

export const mapRemainsToDiscounts = (
    remains: TInfoTariffRatePlanRemain[] = [],
    translation: IPageSubscriberIdTranslation['tabs']['info']['units'],
): IInfoTariffDiscount[] => {
    return remains.map((remain) => mapRemainToDiscount(remain, translation));
};
